/*
 * NASS Prenatal Guide deck — shared engine for the trimester/postpartum guides.
 * Swipeable 14-slide "stories" deck: phone frame (402x686) centered on desktop
 * with neighbour slides peeking; fullscreen on mobile. Drag / arrows / keyboard /
 * wheel navigation, a localStorage checklist, and per-slide share/download.
 * Reimplemented in vanilla JS from the Claude Design DCLogic component.
 *
 * One engine drives every guide. Per-guide config comes from the root element:
 *   [data-nass-pg]            marker + wiring root
 *   data-program="#A68BA1"    program (accent) colour
 *   data-store="t1"           localStorage key suffix
 * Each shareable slide carries data-share-src (theme asset URL of its PNG card)
 * and data-share-name (download filename). The engine is idempotent per root.
 */
(function () {
  var W = 402, H = 686;

  function initRoot(root) {
    if (!root || root.__nassPgWired) return;
    root.__nassPgWired = true;

    var shell = root.querySelector('[data-pg-shell]');
    var body = root.querySelector('[data-pg-body]');
    var track = root.querySelector('[data-pg-track]');
    var sections = Array.prototype.slice.call(root.querySelectorAll('[data-pg-slide]'));
    var navTitle = root.querySelector('[data-pg-nav-title]');
    var navDeck = root.querySelector('[data-pg-nav-deck]');
    var prevBtn = root.querySelector('[data-pg-prev]');
    var nextBtn = root.querySelector('[data-pg-next]');
    var counterEl = root.querySelector('[data-pg-counter]');
    var shareBtn = root.querySelector('[data-pg-share]');
    var shareIconWrap = root.querySelector('[data-pg-share-icon]');
    var checks = Array.prototype.slice.call(root.querySelectorAll('[data-pg-check]'));
    var COUNT = sections.length;
    if (!track || !COUNT) return;

    var program = root.getAttribute('data-program') || '#A68BA1';
    var store = root.getAttribute('data-store') || 'guide';
    var KEY_SLIDE = 'nassfit-preg-' + store + '-slide';
    var KEY_CHECK = 'nassfit-preg-' + store + '-checklist';

    var ICON_SHARE = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M 10.667 5.467 L 5.333 0 C 3.332 2.04 2.001 3.427 0 5.467 L 0.993 6.494 C 2.793 4.658 2.524 4.898 4.627 2.756 L 4.627 10.667 L 6.04 10.667 L 6.04 2.756 L 9.663 6.494 L 10.667 5.467 Z" fill="currentColor" transform="matrix(1 0 0 1 2.667 0)"/><path d="M 0 0 L 1.333 0 L 1.333 3.556 L 0 3.556 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 1.778 10.667)"/><path d="M 0 0 L 1.333 0 L 1.333 3.556 L 0 3.556 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 12.889 10.667)"/><path d="M 0 0 L 12.444 0 L 12.444 1.333 L 0 1.333 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 1.778 12.889)"/></svg>';
    var ICON_DOWNLOAD = '<svg viewBox="0 0 16 16" width="16" height="16" aria-hidden="true"><path d="M 10.667 5.467 L 5.333 0 C 3.332 2.04 2.001 3.427 0 5.467 L 0.993 6.494 C 2.793 4.658 2.524 4.898 4.627 2.756 L 4.627 10.667 L 6.04 10.667 L 6.04 2.756 L 9.663 6.494 L 10.667 5.467 Z" fill="currentColor" transform="matrix(-1 0 0 -1 13.333 10.667)"/><path d="M 0 0 L 1.333 0 L 1.333 3.556 L 0 3.556 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 1.778 10.667)"/><path d="M 0 0 L 1.333 0 L 1.333 3.556 L 0 3.556 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 12.889 10.667)"/><path d="M 0 0 L 12.444 0 L 12.444 1.333 L 0 1.333 L 0 0 Z" fill="currentColor" transform="matrix(1 0 0 1 1.778 12.889)"/></svg>';

    var i = 0, drag = 0, dragging = false, checked = {}, glide = 520,
        glideEase = 'cubic-bezier(.42,0,.28,1)';
    var vw = window.innerWidth, vh = window.innerHeight;

    try {
      i = parseInt(localStorage.getItem(KEY_SLIDE) || '0', 10) || 0;
      checked = JSON.parse(localStorage.getItem(KEY_CHECK) || '{}') || {};
    } catch (e) {}
    if (i < 0 || i > COUNT - 1) i = 0;

    function isDesktop() { return (vw || window.innerWidth) > 520; }
    function gapPx() { return isDesktop() ? 8 : 4; }

    root.style.setProperty('--program', program);
    try {
      document.documentElement.style.background = program;
      document.body.style.background = program;
    } catch (e) {}

    /* ---- layout (shell / body sizing) ---- */
    function layout() {
      var desktop = isDesktop();
      var scale = desktop ? Math.min(vw / W, (vh - 80) / H) : 1;
      if (desktop) scale = Math.floor(W * scale) / W;
      if (desktop) {
        var mt = Math.max(0, (vh - 80 - H * scale) / 2).toFixed(1);
        shell.style.cssText = 'position:relative;display:flex;flex-direction:column;container-type:size;' +
          'width:' + W + 'px;height:' + H + 'px;overflow:visible;flex:0 0 auto;align-self:flex-start;' +
          'margin-top:' + mt + 'px;transform:scale(' + scale + ');transform-origin:top center;';
        body.style.cssText = 'position:relative;flex:1 1 auto;min-height:0;background:transparent;overflow:visible;margin-top:8px;';
      } else {
        shell.style.cssText = 'position:relative;display:flex;flex-direction:column;container-type:size;width:100%;height:100%;overflow:hidden;';
        body.style.cssText = 'position:relative;flex:1 1 auto;min-height:0;background:transparent;overflow:hidden;';
      }
      track.style.gap = gapPx() + 'px';
    }

    function unit() {
      var w = sections[0] ? sections[0].getBoundingClientRect().width : 1;
      return w + gapPx();
    }

    /* ---- paint track + neighbour fx ---- */
    function paint(idx, dragPx, transition) {
      var gap = gapPx();
      track.style.transition = transition || 'none';
      track.style.transform = 'translate3d(calc(' + (-idx * 100) + '% + ' + (dragPx - idx * gap) + 'px), 0, 0)';
      var desktop = isDesktop();
      var u = unit();
      var pos = idx - dragPx / u;
      var ua = navigator.userAgent || '';
      var iosSafari = /iP(hone|ad|od)/.test(ua) && /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS|OPiOS/.test(ua);
      sections.forEach(function (s, n) {
        s.style.transition = dragging ? 'none' : ('opacity ' + glide + 'ms ' + glideEase);
        if (desktop) s.style.opacity = String(1 - Math.min(1, Math.abs(n - pos)) * 0.65);
        else s.style.opacity = '1';
        if (!dragging) s.style.borderRadius = (desktop || iosSafari) ? '32px' : '0 0 32px 32px';
      });
    }

    /* ---- chrome (nav bars, counter, arrows, share) ---- */
    function renderChrome() {
      var titleShown = i === 0;
      navTitle.style.cssText = 'position:absolute;inset:0;box-sizing:border-box;padding:10px 10px calc(10px + env(safe-area-inset-bottom));display:flex;' +
        'transition:opacity .42s cubic-bezier(.42,0,.3,1), transform .42s cubic-bezier(.42,0,.3,1);' +
        (titleShown ? 'opacity:1;transform:none;pointer-events:auto;' : 'opacity:0;transform:translateY(8px);pointer-events:none;');
      navDeck.style.cssText = 'position:absolute;inset:0;box-sizing:border-box;padding:16px 20px calc(16px + env(safe-area-inset-bottom));display:flex;align-items:center;gap:10px;' +
        'transition:opacity .42s cubic-bezier(.42,0,.3,1) .12s, transform .42s cubic-bezier(.42,0,.3,1) .12s;' +
        (!titleShown ? 'opacity:1;transform:none;pointer-events:auto;' : 'opacity:0;transform:translateY(8px);pointer-events:none;');

      if (counterEl) counterEl.textContent = String(i).padStart(2, '0') + ' / ' + (COUNT - 1);
      setArrow(prevBtn, i > 0);
      setArrow(nextBtn, i < COUNT - 1);

      var desktop = isDesktop();
      if (shareIconWrap) shareIconWrap.innerHTML = desktop ? ICON_DOWNLOAD : ICON_SHARE;
      if (shareBtn) {
        shareBtn.setAttribute('aria-label', desktop ? 'Download slide' : 'Share slide');
        var last = i === COUNT - 1;
        shareBtn.style.cssText = 'display:flex;align-items:center;justify-content:center;width:24px;height:24px;padding:0;border:none;background:transparent;color:#fff;cursor:pointer;transition:opacity .2s ease;' +
          (last ? 'opacity:0;pointer-events:none;' : 'opacity:0.9;');
      }
    }

    function setArrow(btn, enabled) {
      if (!btn) return;
      btn.style.cssText = 'width:48px;height:48px;flex:0 0 48px;padding:0;border:none;border-radius:24px;background:#fff;' +
        'display:flex;align-items:center;justify-content:center;transition:opacity .2s, transform .12s;color:#120E1A;' +
        (enabled ? 'opacity:1;cursor:pointer;' : 'opacity:0.3;cursor:default;');
      btn.disabled = !enabled;
    }

    function render(transition) {
      paint(i, dragging ? drag : 0, transition || (dragging ? 'none' : 'transform ' + glide + 'ms ' + glideEase));
      renderChrome();
    }

    /* ---- navigation ---- */
    var lastGo = 0, tapAt = 0;
    function go(n, force) {
      var t = Math.max(0, Math.min(COUNT - 1, n));
      if (t === i) return;
      if (!force && performance.now() - tapAt < 450) return;
      var now = performance.now();
      var chained = now - lastGo < 560;
      lastGo = now;
      glide = chained ? 300 : 520;
      glideEase = chained ? 'cubic-bezier(0,0,.32,1)' : 'cubic-bezier(.42,0,.28,1)';
      i = t; drag = 0;
      try { localStorage.setItem(KEY_SLIDE, String(i)); } catch (e) {}
      render();
      prefetch(i);
    }

    /* ---- checklist ---- */
    function applyChecks() {
      checks.forEach(function (btn) {
        var idx = btn.getAttribute('data-pg-check');
        var on = !!checked[idx];
        btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        var boxEl = btn.querySelector('[data-pg-box]');
        btn.style.opacity = on ? '0.72' : '1';
        if (boxEl) {
          boxEl.style.background = on ? program : 'transparent';
          boxEl.style.border = on ? ('2px solid ' + program) : '2px solid #DCDCDC';
          boxEl.style.color = on ? '#fff' : 'transparent';
        }
      });
    }
    checks.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var idx = btn.getAttribute('data-pg-check');
        checked[idx] = !checked[idx];
        try { localStorage.setItem(KEY_CHECK, JSON.stringify(checked)); } catch (e) {}
        applyChecks();
      });
    });

    /* ---- share / download ---- */
    var fileCache = null;
    function curShareSrc() {
      var s = sections[i];
      return s ? s.getAttribute('data-share-src') : null;
    }
    function curShareName() {
      var s = sections[i];
      return (s && s.getAttribute('data-share-name')) || ('NASS FIT Guide Slide ' + (i + 1) + '.png');
    }
    function prefetch(idx) {
      var s = sections[idx];
      var src = s && s.getAttribute('data-share-src');
      if (!src) return;
      if (fileCache && fileCache.i === idx) return;
      fileCache = { i: idx, file: null, blob: null };
      var cache = fileCache;
      fetch(src).then(function (r) { return r.ok ? r.blob() : null; }).then(function (blob) {
        if (!blob || fileCache !== cache) return;
        cache.blob = blob;
        try { cache.file = new File([blob], curShareName(), { type: 'image/png' }); } catch (e) {}
      }).catch(function () {});
    }
    function download(blob, name) {
      var href = URL.createObjectURL(blob);
      var a = document.createElement('a');
      a.href = href; a.download = name;
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(function () { URL.revokeObjectURL(href); }, 4000);
    }
    function doShare() {
      var src = curShareSrc();
      if (!src) return;
      var name = curShareName();
      var cache = fileCache && fileCache.i === i ? fileCache : null;
      var canShareFile = !isDesktop() && cache && cache.file &&
        navigator.canShare && navigator.canShare({ files: [cache.file] });
      if (canShareFile) {
        navigator.share({ files: [cache.file] }).catch(function () {
          if (cache.blob) download(cache.blob, name);
        });
        return;
      }
      if (cache && cache.blob) { download(cache.blob, name); return; }
      fetch(src).then(function (r) { return r.ok ? r.blob() : Promise.reject(); }).then(function (blob) {
        download(blob, name);
      }).catch(function () {
        if (navigator.clipboard) navigator.clipboard.writeText(location.href).catch(function () {});
      });
    }
    if (shareBtn) shareBtn.addEventListener('click', doShare);

    /* ---- pointer / swipe ---- */
    var startX = 0, startY = 0, lock = null, dstart = 0, lastX = 0, tPrev = 0, vel = 0;
    root.addEventListener('pointerdown', function (e) {
      if (e.target.closest && e.target.closest('a,button')) return;
      if (e.isPrimary === false) return;
      startX = e.clientX; startY = e.clientY; lock = null; dstart = 0;
      tPrev = performance.now(); lastX = e.clientX; vel = 0;
      dragging = true; drag = 0;
    });
    root.addEventListener('pointermove', function (e) {
      if (!dragging) return;
      var dx = e.clientX - startX, dy = e.clientY - startY;
      if (lock === null && (Math.abs(dx) > 8 || Math.abs(dy) > 8)) lock = Math.abs(dx) > Math.abs(dy) ? 'x' : 'y';
      if (lock !== 'x') return;
      var now = performance.now();
      var dt = now - tPrev;
      if (dt > 0) vel = (e.clientX - lastX) / dt;
      tPrev = now; lastX = e.clientX;
      var d = dx;
      if ((i === 0 && d > 0) || (i === COUNT - 1 && d < 0)) d = d * 0.3;
      dstart = d; drag = d;
      paint(i, d, 'none');
    });
    function pointerUp() {
      if (!dragging) return;
      if (lock !== 'x') { drag = 0; vel = 0; tapAt = performance.now(); dragging = false; paint(i, 0, 'none'); return; }
      var w = vw || window.innerWidth;
      var d = dstart || 0;
      var t = Math.max(28, w * 0.1);
      var v = vel || 0;
      var i0 = i;
      var target = i0;
      var fast = Math.abs(v) > 0.28 && Math.abs(d) > 24;
      if (d < -t || (fast && d < 0)) target = i0 + 1;
      else if (d > t || (fast && d > 0)) target = i0 - 1;
      target = Math.max(0, Math.min(COUNT - 1, target));
      var u = unit();
      var remaining = Math.abs((target - i0) * u - d);
      var speed = Math.min(3.2, Math.max(0.7, Math.abs(v)));
      var dur = Math.round(Math.max(200, Math.min(520, remaining / speed)));
      var ease = 'transform ' + dur + 'ms cubic-bezier(.42,0,.28,1)';
      drag = 0; vel = 0; dragging = false; glide = dur; glideEase = 'cubic-bezier(.42,0,.28,1)';
      i = target;
      try { localStorage.setItem(KEY_SLIDE, String(target)); } catch (e) {}
      paint(target, 0, ease);
      renderChrome();
      prefetch(i);
    }
    root.addEventListener('pointerup', pointerUp);
    root.addEventListener('pointercancel', pointerUp);

    /* desktop: click a peeking neighbour to bring it in */
    root.addEventListener('click', function (e) {
      if (!isDesktop()) return;
      if (e.target.closest && e.target.closest('a,button')) return;
      var sec = e.target.closest && e.target.closest('[data-pg-slide]');
      if (!sec) return;
      var n = sections.indexOf(sec);
      if (n < 0 || n === i) return;
      go(n, true);
    });

    /* keyboard + wheel */
    window.addEventListener('keydown', function (e) {
      if (e.isTrusted === false) return;
      if (e.key === 'ArrowRight') go(i + 1);
      if (e.key === 'ArrowLeft') go(i - 1);
    });
    var wheelAt = 0;
    root.addEventListener('wheel', function (e) {
      if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return;
      var now = performance.now();
      if (now - wheelAt < 480) return;
      if (Math.abs(e.deltaX) < 18) return;
      wheelAt = now;
      go(i + (e.deltaX > 0 ? 1 : -1));
    }, { passive: true });

    if (prevBtn) prevBtn.addEventListener('click', function () { go(i - 1); });
    if (nextBtn) nextBtn.addEventListener('click', function () { go(i + 1); });
    var titleNext = root.querySelector('[data-pg-title-next]');
    if (titleNext) titleNext.addEventListener('click', function () { go(i + 1, true); });

    /* resize */
    window.addEventListener('resize', function () {
      vw = window.innerWidth; vh = window.innerHeight;
      layout();
      render('none');
    });

    /* init */
    layout();
    render('none');
    applyChecks();
    prefetch(i);
  }

  function boot() {
    Array.prototype.slice.call(document.querySelectorAll('[data-nass-pg]')).forEach(initRoot);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
